/**
 * Onboarding Page
 * Initial profile setup form
 */

import { storage, requireAuth } from '../utils/storage.js';
import { renderNavbar } from '../components/navbar.js';
import { profileApi } from '../services/api.js';

const CCAA_OPTIONS = [
  'Andalucía',
  'Aragón',
  'Asturias',
  'Baleares',
  'Canarias',
  'Cantabria',
  'Castilla-La Mancha',
  'Castilla y León',
  'Cataluña',
  'Comunidad Valenciana',
  'Extremadura',
  'Galicia',
  'La Rioja',
  'Madrid',
  'Murcia',
  'Navarra',
  'País Vasco',
  'Ceuta',
  'Melilla'
];

const EMPLOYMENT_STATUS = [
  { value: 'employed_indefinite', label: 'Contrato indefinido' },
  { value: 'employed_temporary', label: 'Contrato temporal' },
  { value: 'self_employed', label: 'Autónomo/a' },
  { value: 'civil_servant', label: 'Funcionario/a' },
  { value: 'unemployed', label: 'Desempleado/a' },
  { value: 'student', label: 'Estudiante' },
  { value: 'retired', label: 'Jubilado/a' },
  { value: 'other', label: 'Otros' }
];

export function renderOnboarding(container) {
  if (!requireAuth()) return;

  const user = storage.getUser();

  container.innerHTML = `
    <div class="auth-page" style="padding-top: 40px;">
      <div class="auth-container" style="max-width: 560px;">
        <div class="auth-card animate-slideUp" style="padding: 40px;">
          <div class="auth-header" style="margin-bottom: 32px;">
            <div class="auth-logo">
              <div style="width: 64px; height: 64px; background: var(--primary-100); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
                <i data-feather="user-check" style="color: var(--primary-600); width: 28px; height: 28px;"></i>
              </div>
            </div>
            <h1 class="auth-title">Cuéntanos sobre ti</h1>
            <p class="auth-subtitle">
              Con esta información calcularemos tu situación financiera y te daremos recomendaciones personalizadas.
            </p>
          </div>
          
          <form id="onboardingForm">
            <div class="form-group">
              <label class="form-label" for="age">
                <i data-feather="calendar" style="width: 14px; height: 14px; margin-right: 4px;"></i>
                Edad
              </label>
              <input 
                type="number" 
                id="age" 
                class="form-input" 
                placeholder="Ej: 28"
                required
                min="18"
                max="100"
              />
            </div>
            
            <div class="form-group">
              <label class="form-label" for="employmentStatus">
                <i data-feather="briefcase" style="width: 14px; height: 14px; margin-right: 4px;"></i>
                Situación laboral
              </label>
              <select id="employmentStatus" class="form-select" required>
                <option value="">Selecciona una opción</option>
                ${EMPLOYMENT_STATUS.map(status => `
                  <option value="${status.value}">${status.label}</option>
                `).join('')}
              </select>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="monthlyIncome">
                <i data-feather="dollar-sign" style="width: 14px; height: 14px; margin-right: 4px;"></i>
                Ingresos netos mensuales (€)
              </label>
              <input 
                type="number" 
                id="monthlyIncome" 
                class="form-input" 
                placeholder="Ej: 1800"
                required
                min="0"
                step="50"
              />
              <small style="color: var(--gray-500); font-size: 12px;">
                Suma de todos tus ingresos netos al mes
              </small>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="currentSavings">
                <i data-feather="piggy-bank" style="width: 14px; height: 14px; margin-right: 4px;"></i>
                Ahorros actuales (€)
              </label>
              <input 
                type="number" 
                id="currentSavings" 
                class="form-input" 
                placeholder="Ej: 15000"
                required
                min="0"
                step="100"
              />
              <small style="color: var(--gray-500); font-size: 12px;">
                Dinero que tienes ahorrado actualmente
              </small>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="region">
                <i data-feather="map-pin" style="width: 14px; height: 14px; margin-right: 4px;"></i>
                Comunidad Autónoma
              </label>
              <select id="region" class="form-select" required>
                <option value="">Selecciona tu CCAA</option>
                ${CCAA_OPTIONS.map(ccaa => `
                  <option value="${ccaa}">${ccaa}</option>
                `).join('')}
              </select>
              <small style="color: var(--gray-500); font-size: 12px;">
                Para mostrarte ayudas específicas de tu zona
              </small>
            </div>
            
            <div id="onboardingError" class="alert alert-error hidden" style="margin-bottom: 16px;">
              <i data-feather="alert-circle"></i>
              <span id="onboardingErrorText"></span>
            </div>
            
            <button type="submit" class="btn btn-primary btn-lg w-full" style="margin-top: 24px;">
              <i data-feather="arrow-right"></i>
              Continuar al Dashboard
            </button>
          </form>
          
          <div style="text-align: center; margin-top: 24px; padding-top: 24px; border-top: 1px solid var(--gray-100);">
            <p style="font-size: 12px; color: var(--gray-500);">
              <i data-feather="lock" style="width: 12px; height: 12px; margin-right: 4px;"></i>
              Tus datos están protegidos según el RGPD y nunca serán compartidos con terceros.
            </p>
          </div>
        </div>
      </div>
    </div>
  `;

  initOnboardingForm();
}

function initOnboardingForm() {
  const form = document.getElementById('onboardingForm');
  const errorDiv = document.getElementById('onboardingError');
  const errorText = document.getElementById('onboardingErrorText');
  const submitBtn = form.querySelector('button[type="submit"]');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const age = parseInt(document.getElementById('age').value);
    const employmentStatus = document.getElementById('employmentStatus').value;
    const monthlyIncome = parseFloat(document.getElementById('monthlyIncome').value);
    const currentSavings = parseFloat(document.getElementById('currentSavings').value);
    const region = document.getElementById('region').value;

    // Validation
    if (age < 18 || age > 100) {
      errorText.textContent = 'La edad debe estar entre 18 y 100 años';
      errorDiv.classList.remove('hidden');
      return;
    }

    if (!employmentStatus) {
      errorText.textContent = 'Selecciona tu situación laboral';
      errorDiv.classList.remove('hidden');
      return;
    }

    if (!region) {
      errorText.textContent = 'Selecciona tu Comunidad Autónoma';
      errorDiv.classList.remove('hidden');
      return;
    }

    // Show loading state
    const originalBtnContent = submitBtn.innerHTML;
    submitBtn.innerHTML = '<div class="spinner" style="width: 20px; height: 20px; border-width: 2px;"></div> Guardando...';
    submitBtn.disabled = true;
    errorDiv.classList.add('hidden');

    // Profile data to save
    const profileData = {
      age,
      employmentStatus,
      monthlyIncome,
      currentSavings,
      region,
    };

    try {
      // Send to backend API
      const savedProfile = await profileApi.saveProfile(profileData);

      // Save profile locally (with data from backend or local)
      const profile = {
        id: savedProfile?.id || 'profile_' + Date.now(),
        userId: storage.getUser()?.id,
        ...profileData,
        createdAt: savedProfile?.createdAt || new Date().toISOString()
      };

      storage.setProfile(profile);

      // Initialize guide progress
      storage.setGuideProgress({ step: 1, completed: false });

      // Navigate to dashboard
      window.router.navigate('/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);

      // If backend fails, still save locally and continue
      const profile = {
        id: 'profile_' + Date.now(),
        userId: storage.getUser()?.id,
        ...profileData,
        createdAt: new Date().toISOString()
      };

      storage.setProfile(profile);

      // Initialize guide progress
      storage.setGuideProgress({ step: 1, completed: false });

      // Navigate to dashboard even if backend fails
      window.router.navigate('/dashboard');
    }
  });
}
